import type {
  FairUseOverviewRow,
  FairUsePersonIdentity,
  FairUsePersonRow,
} from "@/api/admin/fair-use"
import type { LocalAiLeadMapping } from "@/api/admin-api"

/**
 * An overview row, as the people table's row shape.
 *
 * The filtered views ("Requests waiting", "Near their budget", "Stopped",
 * "Custom plan") are questions about a MONTH, so they keep answering from the
 * overview — every row they can return is by definition a row with usage.
 * `has_usage: true` is therefore a statement of fact, not a default.
 */
export function asFairUsePersonRow(row: FairUseOverviewRow): FairUsePersonRow {
  return { ...row, has_usage: true }
}

/** One person's standing in the local-AI-lead roster, as the badge needs it. */
export type FairUseLeadInfo = {
  /** This user IS a local AI lead. */
  isLead: boolean
  /** Distinct users assigned to them; 0 when they are not a lead. */
  assignedCount: number
  /** The lead(s) THIS user answers to; empty when nobody leads them. */
  leadOf: Array<{ oid: string; name: string }>
}

const NO_LEAD: FairUseLeadInfo = { isLead: false, assignedCount: 0, leadOf: [] }

function sameOid(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false
  return a.toLocaleLowerCase() === b.toLocaleLowerCase()
}

/**
 * What to badge a row with.
 *
 * The overview stamps `is_lead` / `lead_assigned_count` / `lead_of` when it
 * could read the roster, and leaves all three OFF when it could not — absent
 * is "unknown", not "no". So a present `is_lead` is authoritative, and only a
 * row without it falls back to joining the roster the tab has already loaded
 * for the management card below. That fallback also covers a backend from
 * before round 4, which never stamps the fields at all.
 */
export function fairUseRowLeadInfo(
  row: FairUsePersonIdentity,
  mappings?: readonly LocalAiLeadMapping[]
): FairUseLeadInfo {
  if (row.is_lead !== undefined) {
    return {
      isLead: row.is_lead,
      assignedCount: row.lead_assigned_count ?? 0,
      leadOf: row.lead_of ?? [],
    }
  }
  if (!mappings || mappings.length === 0) return NO_LEAD

  let isLead = false
  let assignedCount = 0
  const leadOf: Array<{ oid: string; name: string }> = []
  for (const mapping of mappings) {
    if (sameOid(mapping.ambassadorOid, row.user)) {
      isLead = true
      // Distinct, because a roster document may list the same oid twice.
      assignedCount = new Set(mapping.assignedUsers.map((user) => user.oid.toLocaleLowerCase()))
        .size
    }
    if (mapping.assignedUsers.some((user) => sameOid(user.oid, row.user))) {
      leadOf.push({ oid: mapping.ambassadorOid, name: mapping.ambassadorName })
    }
  }
  return { isLead, assignedCount, leadOf }
}
